'use client';

import { c as _c } from "react/compiler-runtime";
import { BuilderBlocks } from '@builder.io/react';
import { type CSSProperties, type ReactNode } from 'react';
import { useBreakpoints } from '@src/utils/breakpoints.util';
import { getBuilderPreviewMode } from '@src/utils/builder.util';
import { cn } from '@src/utils/tailwind';
import { VideoPlayer } from '../VideoPlayer/VideoPlayer';
import type { ContentSides, ContentSizes, ImagePositions, ImageSizes, ImageTransformOrigins, MediaTypes, PaddingYVariants } from './SplitContent.builder';
export interface SplitContentProps {
  attributes?: any;
  backgroundColor?: string;
  backgroundImageUrl?: string;
  backgroundPosition?: (typeof ImagePositions)[number];
  backgroundSize?: (typeof ImageSizes)[number];
  brightcoveVideoId?: string;
  builderBlock?: any;
  children?: ReactNode;
  contentSide?: (typeof ContentSides)[number];
  contentSize?: (typeof ContentSizes)[number];
  mediaType?: (typeof MediaTypes)[number];
  mediaBlocks?: ReactNode;
  mobileBackgroundColor?: SplitContentProps['backgroundColor'];
  mobileBackgroundHidden?: boolean;
  mobileBackgroundImageUrl?: SplitContentProps['backgroundImageUrl'];
  mobileBackgroundPosition?: SplitContentProps['backgroundPosition'];
  mobileBackgroundSize?: SplitContentProps['backgroundSize'];
  mobileContentSide?: SplitContentProps['contentSide'];
  mobileFlip?: boolean;
  mobilePhotoHeight?: number;
  mobilePhotoHidden?: boolean;
  mobilePhotoPosition?: SplitContentProps['photoPosition'];
  mobilePhotoScale?: SplitContentProps['photoScale'];
  mobilePhotoSize?: SplitContentProps['photoSize'];
  mobilePhotoTransformOriginY?: (typeof ImageTransformOrigins)[number];
  mobilePhotoUrl?: SplitContentProps['photoUrl'];
  mobileRoundedNone?: boolean;
  photoPosition?: (typeof ImagePositions)[number];
  photoScale?: number;
  photoSize?: (typeof ImageSizes)[number];
  photoTransformOriginY?: (typeof ImageTransformOrigins)[number];
  photoUrl?: string;
  playAsPopup?: boolean;
  roundedNone?: boolean;
  paddingYVariant?: (typeof PaddingYVariants)[number];
  videoSEO?: SplitContentVideoSEOData;
}
export interface SplitContentVideoSEOData {
  name?: string;
  description?: string;
  thumbnailUrl?: string;
  uploadDate?: string;
  duration?: string;
  embedUrl?: string;
}
export function SplitContent(props) {
  const $ = _c(98);
  const {
    isMobile,
    isTablet
  } = useBreakpoints();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getBuilderPreviewMode();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    edit
  } = t0;
  let t1;
  if ($[1] !== isMobile || $[2] !== isTablet || $[3] !== props.attributes || $[4] !== props.backgroundColor || $[5] !== props.backgroundImageUrl || $[6] !== props.backgroundPosition || $[7] !== props.backgroundSize || $[8] !== props.brightcoveVideoId || $[9] !== props.builderBlock?.id || $[10] !== props.children || $[11] !== props.contentSide || $[12] !== props.contentSize || $[13] !== props.mediaBlocks || $[14] !== props.mediaType || $[15] !== props.mobileBackgroundColor || $[16] !== props.mobileBackgroundHidden || $[17] !== props.mobileBackgroundImageUrl || $[18] !== props.mobileBackgroundPosition || $[19] !== props.mobileBackgroundSize || $[20] !== props.mobileContentSide || $[21] !== props.mobileFlip || $[22] !== props.mobilePhotoHeight || $[23] !== props.mobilePhotoHidden || $[24] !== props.mobilePhotoPosition || $[25] !== props.mobilePhotoScale || $[26] !== props.mobilePhotoSize || $[27] !== props.mobilePhotoTransformOriginY || $[28] !== props.mobilePhotoUrl || $[29] !== props.mobileRoundedNone || $[30] !== props.paddingYVariant || $[31] !== props.photoPosition || $[32] !== props.photoScale || $[33] !== props.photoSize || $[34] !== props.photoTransformOriginY || $[35] !== props.photoUrl || $[36] !== props.playAsPopup || $[37] !== props.roundedNone || $[38] !== props.videoSEO?.description || $[39] !== props.videoSEO?.duration || $[40] !== props.videoSEO?.embedUrl || $[41] !== props.videoSEO?.name || $[42] !== props.videoSEO?.thumbnailUrl || $[43] !== props.videoSEO?.uploadDate) {
    const contentSide = getProp(props.contentSide, props.mobileContentSide) ?? "left";
    const backgroundColor = getProp(props.backgroundColor, props.mobileBackgroundColor);
    const backgroundImageUrl = getProp(props.backgroundImageUrl, props.mobileBackgroundImageUrl);
    const backgroundSize = getProp(props.backgroundSize, props.mobileBackgroundSize);
    const backgroundPosition = getProp(props.backgroundPosition, props.mobileBackgroundPosition);
    const backgroundHidden = getProp(undefined, props.mobileBackgroundHidden) ?? false;
    const photoUrl = getProp(props.photoUrl, props.mobilePhotoUrl);
    const photoSize = getProp(props.photoSize, props.mobilePhotoSize);
    const photoPosition = getProp(props.photoPosition, props.mobilePhotoPosition);
    const photoTransformOriginY = getProp(props.photoTransformOriginY, props.mobilePhotoTransformOriginY);
    const photoScale = getProp(props.photoScale, props.mobilePhotoScale);
    const photoHeight = getProp(undefined, props.mobilePhotoHeight);
    const photoHidden = getProp(undefined, props.mobilePhotoHidden);
    const paddingY = props.paddingYVariant ?? "large";
    const isMediaVideo = props.mediaType === "video";
    const isMediaPhoto = props.mediaType === "photo";
    const isMediaBlocks = props.mediaType === "blocks";
    const seoTemplate = {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      name: props.videoSEO?.name,
      description: props.videoSEO?.description,
      thumbnailUrl: props.videoSEO?.thumbnailUrl,
      uploadDate: props.videoSEO?.uploadDate,
      duration: props.videoSEO?.duration,
      embedUrl: props.videoSEO?.embedUrl
    };
    function getProp(prop, mobileProp) {
      return isMobile || isTablet ? mobileProp || prop : prop;
    }
    const getPhoto = function getPhoto(side) {
      if (contentSide === side || !isMediaPhoto) {
        return;
      }
      return {
        backgroundImage: photoUrl ? `url('${photoUrl}?format=webp')` : undefined,
        backgroundSize: photoSize ?? "cover",
        backgroundPosition: photoPosition ?? "center",
        height: photoHeight ?? "auto",
        transform: `scale(${photoScale ?? 1})`,
        display: photoHidden ? "none" : undefined,
        transformOrigin: `center ${photoTransformOriginY}`
      } satisfies CSSProperties;
    };
    const getContentSize = function getContentSize(side_0) {
      const forContent = contentSide === side_0;
      const size = forContent ? props.contentSize ?? 6 : 12 - (props.contentSize ?? 6);
      return {
        gridColumn: `span ${size}`
      };
    };
    let t2;
    if ($[45] !== isMediaBlocks || $[46] !== isMediaVideo || $[47] !== props.brightcoveVideoId || $[48] !== props.builderBlock?.id || $[49] !== props.mediaBlocks || $[50] !== props.playAsPopup) {
      t2 = function renderContent() {
        if (isMediaVideo && props.brightcoveVideoId) {
          return <div className="flex h-full items-center justify-center"><VideoPlayer videoId={props.brightcoveVideoId} autoplay={false} playAsPopup={props.playAsPopup} /></div>;
        }
        if (isMediaBlocks) {
          return <BuilderBlocks parentElementId={props.builderBlock?.id} dataPath="component.options.mediaBlocks" blocks={props.mediaBlocks} className="size-full" />;
        }
      };
      $[45] = isMediaBlocks;
      $[46] = isMediaVideo;
      $[47] = props.brightcoveVideoId;
      $[48] = props.builderBlock?.id;
      $[49] = props.mediaBlocks;
      $[50] = props.playAsPopup;
      $[51] = t2;
    } else {
      t2 = $[51];
    }
    const renderContent = t2;
    let t3;
    if ($[52] !== isMediaVideo) {
      t3 = edit && isMediaVideo && <div className="pointer-events-none flex items-center justify-center bg-yellow-600/40 p-2 text-center text-body-base text-black opacity-70">NOTE: this helper text only shows within the Builder editor - This component has video content, please make sure to update the Video SEO data in the component Block Options.</div>;
      $[52] = isMediaVideo;
      $[53] = t3;
    } else {
      t3 = $[53];
    }
    const t4 = props?.attributes?.["data-testid"];
    const t5 = props?.attributes?.["builder-id"];
    const t6 = !props.roundedNone;
    const t7 = !props.mobileRoundedNone;
    let t8;
    if ($[54] !== props.attributes?.className || $[55] !== props.mobileRoundedNone || $[56] !== props.roundedNone || $[57] !== t6 || $[58] !== t7) {
      t8 = cn("grid w-full auto-rows-min grid-cols-1 bg-slate-400 bg-cover bg-center bg-no-repeat lg:min-h-[448px] lg:grid-cols-12 lg:grid-rows-1", {
        "lg:rounded-none": props.roundedNone,
        "lg:rounded-[33px]": t6,
        "rounded-none": props.mobileRoundedNone,
        "rounded-[33px]": t7
      }, props?.attributes?.className);
      $[54] = props.attributes?.className;
      $[55] = props.mobileRoundedNone;
      $[56] = props.roundedNone;
      $[57] = t6;
      $[58] = t7;
      $[59] = t8;
    } else {
      t8 = $[59];
    }
    const t9 = {
      backgroundColor,
      backgroundSize: backgroundSize ?? "cover",
      backgroundPosition: backgroundPosition ?? "center",
      backgroundImage: !backgroundHidden && backgroundImageUrl ? `url('${backgroundImageUrl}?format=webp')` : undefined,
      ...props?.attributes?.style
    };
    const t10 = isMediaVideo && contentSide === "right";
    const t11 = paddingY === "large";
    const t12 = paddingY === "medium";
    let t13;
    if ($[60] !== props.mobileFlip || $[61] !== props.mobileRoundedNone || $[62] !== props.roundedNone || $[63] !== t10 || $[64] !== t11 || $[65] !== t12) {
      t13 = cn("rounded-t-[33px] bg-cover bg-center bg-no-repeat px-6 lg:h-full lg:rounded-none lg:rounded-l-[33px] lg:px-16", {
        "rounded-none rounded-b-[33px]": props.mobileFlip,
        "lg:py-0": t10,
        "rounded-none": props.mobileRoundedNone,
        "lg:rounded-none": props.roundedNone,
        "py-8 lg:py-28": t11,
        "py-6 lg:py-16": t12
      });
      $[60] = props.mobileFlip;
      $[61] = props.mobileRoundedNone;
      $[62] = props.roundedNone;
      $[63] = t10;
      $[64] = t11;
      $[65] = t12;
      $[66] = t13;
    } else {
      t13 = $[66];
    }
    const t14 = <section style={{
      ...getPhoto("left"),
      ...getContentSize("left")
    }} className={t13}>{contentSide === "right" && renderContent()}{contentSide === "left" && <BuilderBlocks parentElementId={props.builderBlock?.id} dataPath="component.options.children" blocks={props.children} className="size-full" />}</section>;
    const t15 = getPhoto("right");
    let t16;
    if ($[67] !== getContentSize) {
      t16 = getContentSize("right");
      $[67] = getContentSize;
      $[68] = t16;
    } else {
      t16 = $[68];
    }
    let t17;
    if ($[69] !== t15 || $[70] !== t16) {
      t17 = {
        ...t15,
        ...t16
      };
      $[69] = t15;
      $[70] = t16;
      $[71] = t17;
    } else {
      t17 = $[71];
    }
    const t18 = paddingY === "large";
    const t19 = paddingY === "medium";
    const t20 = isMediaVideo && contentSide === "left";
    let t21;
    if ($[72] !== props.mobileFlip || $[73] !== props.mobileRoundedNone || $[74] !== props.roundedNone || $[75] !== t18 || $[76] !== t19 || $[77] !== t20) {
      t21 = cn("rounded-b-[33px] bg-cover bg-center bg-no-repeat px-6 lg:h-full lg:rounded-none lg:rounded-r-[33px] lg:px-16", {
        "order-first rounded-none rounded-t-[33px] lg:order-last": props.mobileFlip,
        "rounded-none": props.mobileRoundedNone,
        "lg:rounded-none": props.roundedNone,
        "py-8 lg:py-28": t18,
        "py-6 lg:py-16": t19,
        "lg:py-0": t20
      });
      $[72] = props.mobileFlip;
      $[73] = props.mobileRoundedNone;
      $[74] = props.roundedNone;
      $[75] = t18;
      $[76] = t19;
      $[77] = t20;
      $[78] = t21;
    } else {
      t21 = $[78];
    }
    let t22;
    if ($[79] !== contentSide || $[80] !== renderContent) {
      t22 = contentSide === "left" && renderContent();
      $[79] = contentSide;
      $[80] = renderContent;
      $[81] = t22;
    } else {
      t22 = $[81];
    }
    let t23;
    if ($[82] !== contentSide || $[83] !== props.builderBlock?.id || $[84] !== props.children) {
      t23 = contentSide === "right" && <BuilderBlocks parentElementId={props.builderBlock?.id} dataPath="component.options.children" blocks={props.children} className="size-full" />;
      $[82] = contentSide;
      $[83] = props.builderBlock?.id;
      $[84] = props.children;
      $[85] = t23;
    } else {
      t23 = $[85];
    }
    let t24;
    if ($[86] !== t17 || $[87] !== t21 || $[88] !== t22 || $[89] !== t23) {
      t24 = <section style={t17} className={t21}>{t22}{t23}</section>;
      $[86] = t17;
      $[87] = t21;
      $[88] = t22;
      $[89] = t23;
      $[90] = t24;
    } else {
      t24 = $[90];
    }
    let t25;
    if ($[91] !== t14 || $[92] !== t24 || $[93] !== t4 || $[94] !== t5 || $[95] !== t8 || $[96] !== t9) {
      t25 = <div data-testid={t4} builder-id={t5} className={t8} style={t9}>{t14}{t24}</div>;
      $[91] = t14;
      $[92] = t24;
      $[93] = t4;
      $[94] = t5;
      $[95] = t8;
      $[96] = t9;
      $[97] = t25;
    } else {
      t25 = $[97];
    }
    t1 = <>{t3}{t25}{isMediaVideo && props.videoSEO?.name && <script id="videoData" type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(seoTemplate, null, 2)
      }} />}</>;
    $[1] = isMobile;
    $[2] = isTablet;
    $[3] = props.attributes;
    $[4] = props.backgroundColor;
    $[5] = props.backgroundImageUrl;
    $[6] = props.backgroundPosition;
    $[7] = props.backgroundSize;
    $[8] = props.brightcoveVideoId;
    $[9] = props.builderBlock?.id;
    $[10] = props.children;
    $[11] = props.contentSide;
    $[12] = props.contentSize;
    $[13] = props.mediaBlocks;
    $[14] = props.mediaType;
    $[15] = props.mobileBackgroundColor;
    $[16] = props.mobileBackgroundHidden;
    $[17] = props.mobileBackgroundImageUrl;
    $[18] = props.mobileBackgroundPosition;
    $[19] = props.mobileBackgroundSize;
    $[20] = props.mobileContentSide;
    $[21] = props.mobileFlip;
    $[22] = props.mobilePhotoHeight;
    $[23] = props.mobilePhotoHidden;
    $[24] = props.mobilePhotoPosition;
    $[25] = props.mobilePhotoScale;
    $[26] = props.mobilePhotoSize;
    $[27] = props.mobilePhotoTransformOriginY;
    $[28] = props.mobilePhotoUrl;
    $[29] = props.mobileRoundedNone;
    $[30] = props.paddingYVariant;
    $[31] = props.photoPosition;
    $[32] = props.photoScale;
    $[33] = props.photoSize;
    $[34] = props.photoTransformOriginY;
    $[35] = props.photoUrl;
    $[36] = props.playAsPopup;
    $[37] = props.roundedNone;
    $[38] = props.videoSEO?.description;
    $[39] = props.videoSEO?.duration;
    $[40] = props.videoSEO?.embedUrl;
    $[41] = props.videoSEO?.name;
    $[42] = props.videoSEO?.thumbnailUrl;
    $[43] = props.videoSEO?.uploadDate;
    $[44] = t1;
  } else {
    t1 = $[44];
  }
  return t1;
}